import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { isNativePlatform } from "./lib/native";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AuthCallback from "./pages/AuthCallback";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import Visualization from "./pages/Visualization";
import Progress from "./pages/Progress";
import Settings from "./pages/Settings";
import Premium from "./pages/Premium";
import MyJournal from "./pages/MyJournal";
import AdminModeration from "./pages/AdminModeration";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Support from "./pages/Support";
import OfflineBanner from "./components/OfflineBanner";
import AnalyticsConsentBanner from "./components/AnalyticsConsent";

import "./App.css";

const FullScreenLoader = () => (
  <div className="min-h-screen bg-[#1C1C1E] flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-2 border-[#8BAA9E] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-[#B5B1AB] font-sans">Loading...</p>
    </div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <FullScreenLoader />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Native app: signed-in users skip Landing/Login and go straight to the Dashboard.
const NativeAuthGate = ({ children }) => {
  const { user, loading } = useAuth();
  if (!isNativePlatform()) return children;
  if (loading) return <FullScreenLoader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

// App Router with Auth Check
function AppRouter() {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<NativeAuthGate><Landing /></NativeAuthGate>} />
      <Route path="/login" element={<NativeAuthGate><Login /></NativeAuthGate>} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* Legal — public, MUST remain outside ProtectedRoute and above the
          catch-all so unauthenticated visitors (and App Store reviewers)
          can reach them without being bounced to /login. */}
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      {/* Support — public page used as the App Store Connect "Support URL".
          MUST remain outside ProtectedRoute and above the catch-all. */}
      <Route path="/support" element={<Support />} />
      {/* /guidelines is an orphan from the retired Collective feature.
          Keep it reachable and redirect to /privacy so App Reviewers who
          follow any legacy inbound link do not see a broken page. */}
      <Route path="/guidelines" element={<Navigate to="/privacy" replace />} />

      {/* Protected Routes */}
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <Onboarding />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/visualization" element={
        <ProtectedRoute>
          <Visualization />
        </ProtectedRoute>
      } />
      <Route path="/progress" element={
        <ProtectedRoute>
          <Progress />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />
      <Route path="/premium" element={
        <ProtectedRoute>
          {isNativePlatform() ? (
            /* App Store 3.1.1 / Google Play 4.6: the /premium page uses
               Stripe checkout. On native, force users into Settings where
               NativeSubscribeButton (RevenueCat IAP) is the only path. */
            <Navigate to="/settings" replace />
          ) : (
            <Premium />
          )}
        </ProtectedRoute>
      } />
      <Route path="/collective" element={<Navigate to="/dashboard" replace />} />
      <Route path="/journal" element={
        <ProtectedRoute>
          <MyJournal />
        </ProtectedRoute>
      } />
      {/* Admin-only route — the page itself verifies role via
          /api/admin/moderation/me and shows a friendly Not-available screen
          for ordinary users. Backend endpoints independently enforce 403. */}
      <Route path="/admin/moderation" element={
        <ProtectedRoute>
          <AdminModeration />
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <OfflineBanner />
        <AppRouter />
        <AnalyticsConsentBanner />
        <Toaster 
          position="top-center" 
          toastOptions={{
            style: {
              background: '#252528',
              color: '#F5F3EF',
              border: '1px solid rgba(255, 255, 255, 0.08)',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
